import React from "react";
import PropTypes from "prop-types";
import { Icon, Spin, Empty } from "antd";
import * as XLSX from "xlsx";
import DocumentService from "services/documents-service";
import ExcelView from "./excel-view";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
// Documents open slightly zoomed out so the whole page fits without scrolling.
const DEFAULT_ZOOM = 0.9;
// Appended to PDF iframe srcs so the browser's native PDF viewer opens at 90%.
const PDF_ZOOM_FRAGMENT = "#zoom=90";

// True for Excel/CSV by MIME type or file extension.
function isSpreadsheet(contentType, filePath) {
    if (/spreadsheet|ms-excel|excel|csv/i.test(contentType || "")) {
        return true;
    }
    return /\.(xlsx|xlsm|xls|csv)(\?|$)/i.test(filePath || "");
}

/**
 * Auth-aware KYC document viewer with a zoom / rotate / reset toolbar.
 *
 * Fetches the file's full URL through DocumentService, turns it into a blob URL,
 * and renders an image (with live transform) or a PDF iframe. Object URLs are
 * revoked on unmount/replace.
 */
class KycDocViewer extends React.Component {
    state = {
        loading: true,
        error: false,
        blobUrl: null,
        isPdf: false,
        isExcel: false,
        sheets: null,
        zoom: DEFAULT_ZOOM,
        rotation: 0
    };

    hasValidPath() {
        return typeof this.props.filePath === "string" && this.props.filePath;
    }

    componentDidMount() {
        if (this.props.directUrl) {
            // External/direct URL (e.g. GST pull PDF) — render as-is, no auth
            // fetch needed.
            return;
        }
        if (this.hasValidPath()) {
            this.fetchFile(this.props.filePath);
        } else {
            this.setState({ loading: false });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.filePath !== this.props.filePath) {
            this.revoke();
            this.setState(
                {
                    loading: true,
                    error: false,
                    isExcel: false,
                    sheets: null,
                    zoom: 1,
                    rotation: 0
                },
                () => {
                    if (this.hasValidPath()) {
                        this.fetchFile(this.props.filePath);
                    } else {
                        this.setState({ loading: false });
                    }
                }
            );
        }
    }

    componentWillUnmount() {
        this.revoke();
    }

    revoke() {
        if (this.state.blobUrl) {
            URL.revokeObjectURL(this.state.blobUrl);
        }
    }

    fetchFile(filePath) {
        DocumentService.getDocumentUrl(filePath)
            .then(({ data, headers }) => {
                const contentType = headers["content-type"] || "";
                const u8 = new Uint8Array(data);
                // Keep a blob URL around so the download/open button always works.
                const blobUrl = URL.createObjectURL(
                    new Blob([data], { type: contentType })
                );
                // Excel/CSV won't render in an iframe (the browser downloads it),
                // so parse it in-browser and show it as a table instead. Detect
                // by MIME / extension, and fall back to magic bytes when the URL
                // has neither (e.g. the GST pull URL): PK.. = xlsx/zip, D0CF = xls.
                let excel = isSpreadsheet(contentType, filePath);
                if (!excel) {
                    const ct = contentType.toLowerCase();
                    const isImg = ct.indexOf("image/") === 0;
                    const isPdfType = ct.indexOf("pdf") !== -1;
                    if (!isImg && !isPdfType && u8.length > 3) {
                        const zip = u8[0] === 0x50 && u8[1] === 0x4b;
                        const ole =
                            u8[0] === 0xd0 &&
                            u8[1] === 0xcf &&
                            u8[2] === 0x11 &&
                            u8[3] === 0xe0;
                        excel = zip || ole;
                    }
                }
                if (excel) {
                    try {
                        const wb = XLSX.read(u8, { type: "array" });
                        const sheets = wb.SheetNames.map(name => ({
                            name,
                            rows: XLSX.utils.sheet_to_json(wb.Sheets[name], {
                                header: 1,
                                blankrows: false,
                                defval: ""
                            })
                        }));
                        if (sheets.length) {
                            this.setState({
                                loading: false,
                                error: false,
                                isExcel: true,
                                sheets,
                                blobUrl
                            });
                            return;
                        }
                    } catch (err) {
                        // Not a real spreadsheet — fall through to normal render.
                    }
                }
                this.setState({
                    loading: false,
                    error: false,
                    isExcel: false,
                    isPdf: contentType.includes("pdf"),
                    blobUrl
                });
            })
            .catch(() => this.setState({ loading: false, error: true }));
    }

    zoomBy = delta =>
        this.setState(prev => ({
            zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom + delta))
        }));

    rotateBy = delta =>
        this.setState(prev => ({ rotation: prev.rotation + delta }));

    reset = () => this.setState({ zoom: DEFAULT_ZOOM, rotation: 0 });

    render() {
        const { directUrl } = this.props;
        if (directUrl) {
            return (
                <div className={"cc-docviewer"}>
                    <div className={"cc-docviewer__toolbar"}>
                        <a
                            className={"cc-docviewer__download"}
                            href={directUrl}
                            target={"_blank"}
                            rel={"noreferrer"}
                            title={"Open in new tab"}
                        >
                            <Icon type={"export"} />
                        </a>
                    </div>
                    <div className={"cc-docviewer__stage"}>
                        <iframe
                            title={"document"}
                            className={"cc-docviewer__pdf"}
                            src={directUrl + PDF_ZOOM_FRAGMENT}
                        />
                    </div>
                </div>
            );
        }
        const {
            loading,
            error,
            blobUrl,
            isPdf,
            isExcel,
            sheets,
            zoom,
            rotation
        } = this.state;
        // The custom zoom / rotate / reset toolbar only applies to images. PDFs
        // and Excel render in their own viewers with native controls, so the
        // custom toolbar is hidden for them.
        const isImage = Boolean(blobUrl) && !isPdf && !isExcel;
        if (!this.hasValidPath()) {
            return (
                <div className={"cc-docviewer cc-docviewer--empty"}>
                    <Empty description={"Document not uploaded"} />
                </div>
            );
        }
        return (
            <div className={"cc-docviewer"}>
                {isImage ? (
                    <div className={"cc-docviewer__toolbar"}>
                        <button
                            type={"button"}
                            onClick={() => this.zoomBy(ZOOM_STEP)}
                            disabled={zoom >= MAX_ZOOM}
                            title={"Zoom in"}
                        >
                            <Icon type={"zoom-in"} />
                        </button>
                        <button
                            type={"button"}
                            onClick={() => this.zoomBy(-ZOOM_STEP)}
                            disabled={zoom <= MIN_ZOOM}
                            title={"Zoom out"}
                        >
                            <Icon type={"zoom-out"} />
                        </button>
                        <button
                            type={"button"}
                            onClick={() => this.rotateBy(-90)}
                            title={"Rotate left"}
                        >
                            <Icon type={"undo"} />
                        </button>
                        <button
                            type={"button"}
                            onClick={() => this.rotateBy(90)}
                            title={"Rotate right"}
                        >
                            <Icon type={"redo"} />
                        </button>
                        <button
                            type={"button"}
                            onClick={this.reset}
                            title={"Reset"}
                        >
                            <Icon type={"fullscreen-exit"} />
                        </button>
                        <a
                            className={"cc-docviewer__download"}
                            href={blobUrl}
                            target={"_blank"}
                            rel={"noreferrer"}
                            title={"Open in new tab"}
                        >
                            <Icon type={"export"} />
                        </a>
                    </div>
                ) : null}
                <div className={"cc-docviewer__stage"}>
                    <Spin spinning={loading}>
                        {error ? (
                            <Empty
                                description={
                                    typeof this.props.filePath === "string" &&
                                        this.props.filePath.indexOf("http") === 0 ? (
                                        <span>
                                            Could not preview here.{" "}
                                            <a
                                                href={this.props.filePath}
                                                target={"_blank"}
                                                rel={"noreferrer"}
                                            >
                                                Open in new tab
                                            </a>
                                        </span>
                                    ) : (
                                        "Could not load document"
                                    )
                                }
                            />
                        ) : isExcel && sheets ? (
                            <ExcelView sheets={sheets} />
                        ) : isPdf && blobUrl ? (
                            <iframe
                                title={"document"}
                                className={"cc-docviewer__pdf"}
                                src={blobUrl + PDF_ZOOM_FRAGMENT}
                            />
                        ) : blobUrl ? (
                            <div
                                className={"cc-docviewer__imgwrap"}
                                style={{
                                    width: `${zoom * 100}%`,
                                    height: `${zoom * 100}%`
                                }}
                            >
                                <img
                                    alt={"document"}
                                    className={"cc-docviewer__img"}
                                    src={blobUrl}
                                    style={{
                                        transform: `rotate(${rotation}deg)`
                                    }}
                                />
                            </div>
                        ) : null}
                    </Spin>
                </div>
            </div>
        );
    }
}

KycDocViewer.propTypes = {
    filePath: PropTypes.string,
    directUrl: PropTypes.string
};

export default KycDocViewer;
